import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { UserCircle } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";

const CATEGORY_TITLES = {
  "governing-board": "National Governing Board",
  "local-presidents": "Local Presidents",
  "skills-development": "National Skills Development Commission",
  "past-presidents": "Past National Presidents",
};

export default function TeamPage() {
  const { category } = useParams();
  const [members, setMembers] = useState([]);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBio, setActiveBio] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/team-members/by-category/${category}`),
      api.get(`/pages/by-slug/${category}`).catch(() => ({ data: null })),
    ]).then(([m, p]) => {
      setMembers(m.data);
      setPage(p.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [category]);

  const title = page?.title || CATEGORY_TITLES[category] || category;
  usePageTitle(title);

  return (
    <div data-testid="team-page">
      {/* Hero */}
      <div className="bg-jci-black relative overflow-hidden">
        {page?.image_url ? (
          <div className="relative h-[300px] md:h-[400px]">
            <img src={page.image_url} alt={title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-jci-black/60" />
            <div className="absolute inset-0 flex items-center">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight" data-testid="team-page-title">{title}</h1>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight" data-testid="team-page-title">{title}</h1>
            </div>
          </div>
        )}
        <div className="h-1 bg-gradient-to-r from-jci-blue via-jci-teal to-jci-navy" />
      </div>

      {/* Banner Image (2:1 ratio) */}
      {page?.banner_image_url && (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl pt-10" data-testid="team-banner-section">
          <div className="relative w-full overflow-hidden rounded-2xl shadow-lg" style={{ aspectRatio: '2/1' }}>
            <img
              src={page.banner_image_url}
              alt={`${title} banner`}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="team-banner-image"
            />
          </div>
        </div>
      )}

      {/* Members Grid */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {[1,2,3,4,5,6,7,8].map(i => <Skeleton key={i} className="h-80 rounded-2xl" />)}
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-20">
            <UserCircle className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400">Team members will be added soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8" data-testid="team-members-grid">
            {members.map((member, i) => (
              <div
                key={member.id}
                className="group relative cursor-pointer"
                onClick={() => setActiveBio(activeBio === i ? null : i)}
                data-testid={`team-member-${i}`}
              >
                <div className="relative overflow-hidden rounded-2xl">
                  <img
                    src={member.photo_url || "https://via.placeholder.com/400x400?text=Photo"}
                    alt={member.name}
                    className="w-full h-72 object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  {/* Bio overlay - hover on desktop */}
                  {member.bio && (
                    <div className={`absolute inset-0 bg-jci-black/85 flex items-center justify-center p-5 transition-opacity duration-300 ${
                      activeBio === i ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    }`}>
                      <p className="text-white text-sm text-center leading-relaxed">{member.bio}</p>
                    </div>
                  )}
                </div>
                <div className="mt-4 text-center">
                  <h3 className="font-bold text-jci-black text-base">{member.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">{member.designation}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
